import React, { FC, useRef, useState } from 'react';
import cx from 'clsx';
import moment from 'moment';
import { Icon } from '@mdi/react';
import { mdiCalendarRange, mdiClose } from '@mdi/js';
import { DayPicker } from 'react-day-picker';
import { enGB, fr, nl } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';

import { useOutsideAlerter } from 'lib';
import { useLocale } from 'lib/hooks/useLocale';
import { TranslationFunc } from 'config/i18n';
import styles from './style.module.scss';

/** An empty end is open. */
export type DateRange = { from?: Date; to?: Date };

const locales = { en: enGB, nl, fr };

const format = (date?: Date) => (date ? moment(date).format('DD-MM-YYYY') : '');

/** One input for the period: presets on the left, a calendar to pick days on the right. */
export const DateRangeFilter: FC<{ value: DateRange; onChange: (range: DateRange) => void; since?: Date }> = ({ value, onChange, since }) => {

  const { t, iso } = useLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Inside the modal: a click in the popover is a click inside the modal, so it does not dismiss it.
  useOutsideAlerter(rootRef, () => {

    setOpen(false);
    return false;

  });

  const daysAgo = (days: number) => moment().startOf('day').subtract(days, 'days').toDate();
  const presets: { label: Parameters<TranslationFunc>[0]; range: DateRange }[] = [
    { label: 'Alarm_Detail_Logs_Range_Today', range: { from: daysAgo(0) } },
    { label: 'Alarm_Detail_Logs_Range_Day', range: { from: moment().subtract(24, 'hours').toDate() } },
    { label: 'Alarm_Detail_Logs_Range_Week', range: { from: daysAgo(6) } },
    { label: 'Alarm_Detail_Logs_Range_Month', range: { from: daysAgo(29) } },
  ];
  const chosen = value.from || value.to;

  return (
    <div ref={rootRef} className={styles.range}>
      <button type="button" className={styles.rangeButton} onClick={() => setOpen((current) => !current)}>
        <Icon path={mdiCalendarRange} size="16px"/>
        <span className={cx(styles.rangeText, !chosen && styles.rangePlaceholder)}>
          {chosen ? `${format(value.from)} – ${format(value.to)}` : t('Alarm_Detail_Logs_Range_Placeholder')}
        </span>
      </button>
      {chosen && (
        <button
          type="button"
          className={styles.rangeClear}
          title={t('Alarm_Detail_Logs_Range_Clear')}
          aria-label={t('Alarm_Detail_Logs_Range_Clear')}
          onClick={() => onChange({})}
        >
          <Icon path={mdiClose} size="14px"/>
        </button>
      )}
      {open && (
        <div className={styles.rangePopover}>
          <div className={styles.rangePresets}>
            {presets.map(({ label, range }) => (
              <button
                key={label}
                type="button"
                onClick={() => {

                  onChange(range);
                  setOpen(false);

                }}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <DayPicker
            mode="range"
            locale={locales[iso]}
            weekStartsOn={1}
            // Nothing happened before the alarm was created or in the future: no days and no months outside that.
            // Whole days: today and the creation day themselves stay selectable, whatever the time of day.
            disabled={[{ after: moment().endOf('day').toDate() }, ...(since ? [{ before: moment(since).startOf('day').toDate() }] : [])]}
            fromMonth={since}
            toMonth={new Date()}
            defaultMonth={value.from}
            selected={{ from: value.from, to: value.to }}
            onSelect={(range) => {

              // Whole days: from the start of the first day to the end of the last one.
              onChange({
                from: range?.from && moment(range.from).startOf('day').toDate(),
                to: range?.to && moment(range.to).endOf('day').toDate(),
              });
              if (range?.to) setOpen(false);

            }}
          />
        </div>
      )}
    </div>
  );

};
